import { FC, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Currency } from '@/components/atoms/Currency'
import { WalletHero } from '@/components/atoms/WalletHero'
import { StakeButton } from '@/components/molecules/StakeButton'
import WarningAlert, {
  WarningAlertRef,
} from '@/components/molecules/WarningAlert'
import { DepositBottomSheet } from '@/components/organisms/DepositBottomSheet'

import { DepositUrlError } from '@/lib/hooks/useGetDepositUrl'
import { Routes } from '@/router/routes'
import { toTokenCentsString } from '@/utils/token'
import { isNative } from '@/utils/utils'

import BuyRpkConfirmationPopup from '../BuyRpkConfirmationPopup'
import { DepositButton } from '../DepositButton'
import { StakingVaultButton } from '../StakingVaultButton'
import { WithdrawButton } from '../WithdrawButton'

interface TransactionHistoryHeaderProps {
  balance?: string
  stakedBalance?: string
}

export const TransactionHistoryHeader: FC<TransactionHistoryHeaderProps> = ({
  balance = '0',
  stakedBalance = '0',
}: TransactionHistoryHeaderProps) => {
  const { t } = useTranslation('common')
  const warningRef = useRef<WarningAlertRef>()
  const [buyRpkConfirmationOpen, setBuyRpkConfirmationOpen] = useState(false)
  const [buyRpkError, setBuyRpkError] = useState<DepositUrlError>()
  const history = useHistory()

  const handleBannerClick = useCallback(() => {
    stakedBalance
      ? history.push(Routes.STAKE_VAULT)
      : history.push(Routes.STAKE)
  }, [history, stakedBalance])

  const handleBuyRpkError = useCallback((error: DepositUrlError) => {
    warningRef?.current?.open()
    setBuyRpkError(error)
    setBuyRpkConfirmationOpen(false)
  }, [])

  return (
    <div className="p-4">
      <WalletHero
        handleOnClick={handleBannerClick}
        title={t('wallet.yourBalance')}
        footer={
          !isNative() && <StakingVaultButton stakedAmount={stakedBalance} />
        }
      >
        <div className="flex flex-col pb-2">
          <span
            className="text-[8vw] sm:text-[3rem] font-bold"
            title={toTokenCentsString(balance)}
          >
            <Currency value={balance} isHighlighted />
          </span>
        </div>
        {!isNative() && (
          <div className="flex flex-start flex-wrap gap-x-1 xs:gap-x-2 gap-y-2">
            <StakeButton text={t('wallet.stake.stake')} />
            <DepositButton />
            <WithdrawButton />
          </div>
        )}

        <DepositBottomSheet
          uuid="deposit-bottom-sheet"
          onBuyRpk={() => setBuyRpkConfirmationOpen(true)}
        />

        <BuyRpkConfirmationPopup
          isOpen={buyRpkConfirmationOpen}
          onDismiss={() => setBuyRpkConfirmationOpen(false)}
          onConfirm={() => undefined}
          onError={handleBuyRpkError}
        />

        <WarningAlert
          ref={warningRef}
          title={t('wallet.buyRpk.error.title')}
          description={<span>{t(buyRpkError?.description)}</span>}
          acknowledgeText={t('warningAcknowledgeAlert')}
        />
      </WalletHero>
    </div>
  )
}
